import React, { useCallback, useRef } from 'react';

import { token } from '@atlaskit/tokens';
import CheckboxCheckedIcon from '@atlaskit/icon/core/checkbox-checked';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, Box, xcss } from '@atlaskit/primitives';

import { useIsDisabledContext } from '@atlassian/company-hub-common';

import type { UnsplashResult, SelectedImage } from '../index';

import { UnsplashImageThumbnail } from './Thumbnail/UnsplashImageThumbnail';
import { PlaceholderThumbnail } from './Thumbnail/PlaceholderThumbnail';
import { UploadedImageThumbnail } from './Thumbnail/UploadedImageThumbnail';
import { NoImageThumbnail } from './Thumbnail/NoImageThumbnail';
import { ExternalSiteImageThumbnail } from './Thumbnail/ExternalSiteImageThumbnail';
import { getFirstImage, createUnsplashDefaultImageThumbnail } from './defaultImagePicker-utils';

const ImagesWrapperStyles = xcss({
	marginTop: 'space.100',
	justifyContent: 'space-between',
	paddingTop: 'space.0',
});

const selectedImageTickStyles = xcss({
	position: 'absolute',
	bottom: 'space.0',
	right: 'space.0',

	'::before': {
		content: '""',
		backgroundColor: 'elevation.surface',
		height: '50%',
		width: '50%',
		position: 'absolute',
		right: 'space.050',
		top: 'space.050',
	},
});

const checkboxWrapperStyles = xcss({
	position: 'relative',
});

type UnsplashImage = {
	fullSizeImageUrl: string;
	altDescription?: string;
};

type CustomImage = {
	imageId: string;
};

export enum ThumbnailType {
	UNSPLASH_DEFAULT_IMAGE,
	UNSPLASH_IMAGE,
	CUSTOM_IMAGE,
	EXTERNAL_SITE_IMAGE,
	NO_IMAGE,
}

export type Thumbnail = {
	type: ThumbnailType;
	image: UnsplashResult | UnsplashImage | CustomImage | string | undefined;
};

const isUnsplashDefaultImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashResult | undefined } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_DEFAULT_IMAGE;
};

const isUnsplashImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashImage } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_IMAGE;
};

const isCustomImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: CustomImage } => {
	return thumbnail.type === ThumbnailType.CUSTOM_IMAGE;
};

const isExternalSiteImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: string } => {
	return thumbnail.type === ThumbnailType.EXTERNAL_SITE_IMAGE;
};

const isNoImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: undefined } => {
	return thumbnail.type === ThumbnailType.NO_IMAGE;
};

const createNoImageThumbnail = (): Thumbnail => ({
	type: ThumbnailType.NO_IMAGE,
	image: undefined,
});

export const renderImagePlaceholders = () => {
	return Array(5)
		.fill(null)
		.map((_, index) => <PlaceholderThumbnail key={index} />);
};

export type DefaultImagePickerProps = {
	contentId: string;
	confluencePageId?: string;
	imageAltText?: string;
	selectedImageUrl?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	handleImageSelection: (props: SelectedImage) => void;
	analyticsSource: string;
	isUploadingImage: boolean;
	enablePaddingBottom?: boolean;
	isNoImageAllowed: boolean;
	initialUploadedImageId?: string;
	previewUploadedImageId?: string;
};

/**
 *
 * @param contentId - The sections content id. Used to store all the data for section.
 * @param imageAltText - Text for the image.
 * @param selectedImageUrl - The current selected image url, if one exists. Can be an unsplash image or uploaded image.
 * @param handleImageSelection - Function to handle selecting an image
 * @param initialUnsplashResults - The default unsplash results to populate thumbnails
 * @param analyticsSource - For analytics tracking
 * @param isUploadingImage - Disables the entire component if true
 * @param isNoImageAllowed - Render a thumbnail option for providing no image if true
 * @param initialUploadedImageId - A ref for the initial uploaded image id. To store value between renders. Used to determine first thumbnail
 * @param previewUploadedImageId - Stores image id value when user uploads an image. Used to determine first thumbnail
 * @returns a group of thumbnails for user to select an image.
 */
export const DefaultImagePicker = ({
	contentId,
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	initialUnsplashResults,
	analyticsSource,
	isUploadingImage,
	isNoImageAllowed,
	initialUploadedImageId,
	previewUploadedImageId,
}: DefaultImagePickerProps) => {
	const isDisabled = useIsDisabledContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onImageClick = useCallback(
		({ fullSizeImageUrl, altDescription, downloadLink }: SelectedImage) => {
			if (!isUploadingImage) {
				handleImageSelection({
					fullSizeImageUrl,
					altDescription,
					downloadLink,
				});

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'selected',
						actionSubject: 'image',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplashDefault',
						},
					},
				}).fire();
			}
		},
		[analyticsSource, createAnalyticsEvent, handleImageSelection, isUploadingImage],
	);

	const renderCheckboxIcon = useCallback(() => {
		return (
			<Box xcss={selectedImageTickStyles}>
				<Flex xcss={checkboxWrapperStyles}>
					<CheckboxCheckedIcon label="checkmark" color={token('color.icon.brand')} />
				</Flex>
			</Box>
		);
	}, []);

	const renderGenericUnsplashImage = useCallback(
		({ smallSizeImageUrl, fullSizeImageUrl, downloadLink, description, isSelected, index }) => {
			return (
				<UnsplashImageThumbnail
					key={`thumbnail-${index}`}
					image={smallSizeImageUrl}
					description={description}
					isSelected={isSelected}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl,
							altDescription: description,
							downloadLink,
						})
					}
				>
					{isSelected && renderCheckboxIcon()}
				</UnsplashImageThumbnail>
			);
		},
		[isUploadingImage, onImageClick, renderCheckboxIcon],
	);

	const renderUnsplashDefaultImage = useCallback(
		(image: UnsplashResult, index: number) => {
			const { fullSizeImageUrl, altDescription, downloadLink, smallSizeImageUrl } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: smallSizeImageUrl ?? fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription,
				downloadLink,
				isSelected,
				index,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderUnsplashImage = useCallback(
		(image: UnsplashImage, index: number) => {
			const { fullSizeImageUrl, altDescription } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription ?? '',
				downloadLink: undefined,
				isSelected,
				index,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderExternalSiteImage = useCallback(
		(image: string, index: number) => {
			const isSelectedImage = image === selectedImageUrl;
			return (
				<ExternalSiteImageThumbnail
					key={`thumbnail-${index}`}
					image={image}
					isSelected={isSelectedImage}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: image,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && renderCheckboxIcon()}
				</ExternalSiteImageThumbnail>
			);
		},
		[selectedImageUrl, isUploadingImage, renderCheckboxIcon, onImageClick],
	);

	const renderUploadedImage = useCallback(
		(image: CustomImage, index: number) => {
			const { imageId } = image;
			const isSelectedImage = imageId === selectedImageUrl && index === 0;

			return (
				<UploadedImageThumbnail
					key={`thumbnail-${index}`}
					contentId={contentId}
					imageId={imageId}
					isSelected={isSelectedImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: imageId,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && renderCheckboxIcon()}
				</UploadedImageThumbnail>
			);
		},
		[selectedImageUrl, contentId, onImageClick, renderCheckboxIcon],
	);

	const renderNoImageThumbnail = useCallback(
		(index: number) => {
			const isSelected = selectedImageUrl === '';
			return (
				<NoImageThumbnail
					key={index}
					isSelected={isSelected}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: '',
							altDescription: '',
						})
					}
				>
					{isSelected && renderCheckboxIcon()}
				</NoImageThumbnail>
			);
		},
		[selectedImageUrl, onImageClick, renderCheckboxIcon],
	);

	const firstImage = useRef<Thumbnail>();
	firstImage.current = getFirstImage({
		selectedImageUrl,
		initialUnsplashResults,
		isNoImageAllowed,
		firstImage,
		previewUploadedImageId,
		initialUploadedImageId,
		imageAltText,
	});

	const secondImage = isNoImageAllowed
		? createNoImageThumbnail()
		: createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[1]);

	const imagesToDisplay = [
		firstImage.current,
		secondImage,
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[2]),
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[3]),
		createUnsplashDefaultImageThumbnail(initialUnsplashResults?.[4]),
	];

	const renderThumbnails = useCallback(
		(thumbnail: Thumbnail, index) => {
			// Ordering matters for first image rendering logic
			if (isNoImageThumbnail(thumbnail)) {
				return renderNoImageThumbnail(index);
			}

			if (!thumbnail.image) {
				return <PlaceholderThumbnail key={index} />;
			}

			if (isUnsplashDefaultImageThumbnail(thumbnail)) {
				return renderUnsplashDefaultImage(thumbnail.image, index);
			}

			if (isUnsplashImageThumbnail(thumbnail)) {
				return renderUnsplashImage(thumbnail.image, index);
			}

			if (isExternalSiteImageThumbnail(thumbnail)) {
				return renderExternalSiteImage(thumbnail.image, index);
			}

			if (isCustomImageThumbnail(thumbnail)) {
				return renderUploadedImage(thumbnail.image, index);
			}

			return <PlaceholderThumbnail key={index} />;
		},
		[
			renderNoImageThumbnail,
			renderUnsplashDefaultImage,
			renderUnsplashImage,
			renderUploadedImage,
			renderExternalSiteImage,
		],
	);

	return (
		<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
			{isDisabled ? renderImagePlaceholders() : imagesToDisplay.map(renderThumbnails)}
		</Flex>
	);
};
